/**
 * How the app is running (browser tab, installed to the home screen, or inside the
 * iOS/Android store app) and the install prompt the browser offers.
 */

/** The store apps add this to their user agent (see mobile/capacitor.config.ts) */
export const NATIVE_UA = 'SignMeOutApp';

export const isNativeApp = () => typeof navigator !== 'undefined' && navigator.userAgent.includes(NATIVE_UA);
export const isIOS = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent) && !isNativeApp();
export const isStandalone = () =>
  typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

/** The store app's own start page (school picker), which differs per platform */
export const nativeHome = () => (/iPhone|iPad|iPod/.test(navigator.userAgent) ? 'capacitor://localhost/?switch=1' : 'https://localhost/?switch=1');

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

export const canPromptInstall = () => !!deferred;
export const onInstallChange = (fn: () => void) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};
export async function promptInstall() {
  if (!deferred) return false;
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  listeners.forEach((fn) => fn());
  return outcome === 'accepted';
}

/** Called once at start-up */
export function setUpApp() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((fn) => fn());
  });
  if (isNativeApp()) document.documentElement.classList.add('is-native-app');
  if (isStandalone()) document.documentElement.classList.add('is-standalone');
  // The store apps load pages straight from the school's address and don't need it
  if (import.meta.env.PROD && 'serviceWorker' in navigator && !isNativeApp()) {
    window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
  }
}
